import { useLocation } from "wouter";
import { useUser } from "@clerk/react";
import { useTranslation } from "react-i18next";
import { safeSetItem } from "@/lib/safeStorage";
import { Heart, Leaf } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import QuoteCarousel from "@/components/QuoteCarousel";

const SAGE = "#A8B5A2";
const TERRACOTTA = "#D9B8A3";

const pillBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  padding: "13px 28px",
  borderRadius: "50px",
  fontSize: "14px",
  fontWeight: 300,
  letterSpacing: "0.04em",
  cursor: "pointer",
  transition: "all 0.3s ease",
  border: "1px solid transparent",
};

export default function JoinPage() {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const { isSignedIn } = useUser();

  function navigateAsDonor() {
    if (isSignedIn) navigate("/donor");
    else { safeSetItem("pendingRole", "donor"); navigate("/sign-up"); }
  }

  function navigateAsDebtor() {
    if (isSignedIn) navigate("/debtor");
    else { safeSetItem("pendingRole", "debtor"); navigate("/sign-up"); }
  }

  return (
    <div style={{ minHeight: "100vh", background: "hsl(var(--background))", fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
      <SiteNav />

      {/* Hero / CTA */}
      <section style={{ position: "relative", overflow: "hidden", minHeight: "520px", display: "flex", alignItems: "center" }}>
        <div style={{ position: "absolute", inset: 0 }}>
          <img
            src="https://images.pexels.com/photos/6646926/pexels-photo-6646926.jpeg?auto=compress&cs=tinysrgb&w=1600"
            alt="" aria-hidden
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", objectPosition: "center 30%", filter: "grayscale(0.3)" }}
            loading="lazy"
          />
          <div aria-hidden style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, rgba(10,10,10,0.94) 0%, rgba(10,10,10,0.88) 100%)" }} />
        </div>

        <svg aria-hidden style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", overflow: "visible" }}>
          <circle cx="50%" cy="50%" r="280" fill="none" stroke={SAGE} strokeWidth="0.75" opacity="0.09" />
          <circle cx="72%" cy="38%" r="160" fill="none" stroke={TERRACOTTA} strokeWidth="0.5" opacity="0.07" />
        </svg>

        <div style={{ position: "relative", maxWidth: "600px", margin: "0 auto", textAlign: "center", padding: "96px 20px" }}>
          <h1 style={{ fontSize: "clamp(30px, 5vw, 52px)", fontWeight: 200, color: "#ffffff", letterSpacing: "-0.03em", marginBottom: "16px" }}>
            {t("joinSection.headline")}
          </h1>
          <p style={{ color: "rgba(255,255,255,0.42)", fontWeight: 300, fontSize: "15px", marginBottom: "40px", lineHeight: 1.7 }}>
            {t("joinSection.subtext")}
          </p>
          {isSignedIn ? (
            <button
              style={{ ...pillBtn, background: "#ffffff", color: "#0a0a0a" }}
              onClick={navigateAsDonor}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = `0 0 0 3px rgba(168,181,162,0.4)`; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = "none"; }}
            >
              {t("joinSection.goToDashboard")}
            </button>
          ) : (
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", justifyContent: "center" }}>
              <button
                style={{ ...pillBtn, background: "#ffffff", color: "#0a0a0a" }}
                onClick={() => navigate("/sign-up")}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = `0 0 0 3px rgba(168,181,162,0.4)`; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = "none"; }}
              >
                {t("joinSection.createAccount")}
              </button>
              <button
                style={{ ...pillBtn, background: "transparent", color: "rgba(255,255,255,0.65)", border: "1px solid rgba(255,255,255,0.2)" }}
                onClick={navigateAsDebtor}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,0.07)";
                  (e.currentTarget as HTMLButtonElement).style.borderColor = `rgba(217,184,163,0.4)`;
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = "transparent";
                  (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(255,255,255,0.2)";
                }}
              >
                {t("joinSection.applyForRelief")}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Two-path explainer */}
      <section style={{ padding: "80px 20px" }}>
        <div style={{ maxWidth: "880px", margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "32px" }}>
          {/* Donor path */}
          <div style={{
            padding: "40px 36px",
            borderRadius: "20px",
            border: `1px solid ${SAGE}40`,
            background: `${SAGE}06`,
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}>
            <div style={{
              width: "48px", height: "48px",
              borderRadius: "50%",
              background: `${SAGE}20`,
              border: `1px solid ${SAGE}50`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Heart size={20} color={SAGE} />
            </div>
            <h2 style={{ fontSize: "22px", fontWeight: 200, letterSpacing: "-0.02em", margin: 0 }}>
              I want to donate
            </h2>
            <p style={{ fontSize: "14px", fontWeight: 300, color: "hsl(var(--muted-foreground))", lineHeight: 1.75, margin: 0 }}>
              Browse verified debt pools, contribute any amount, and watch your impact grow. Donations are tax-deductible and 100% go to eliminating real people's debt.
            </p>
            <ul style={{ fontSize: "13px", fontWeight: 300, color: "hsl(var(--muted-foreground))", lineHeight: 2, paddingLeft: "18px", margin: 0 }}>
              <li>Choose pools by category or amount</li>
              <li>Track total debt you've helped erase</li>
              <li>Earn donor recognition tiers</li>
              <li>Receive tax receipts automatically</li>
            </ul>
            <button
              style={{
                ...pillBtn,
                background: "#0a0a0a",
                color: "#ffffff",
                border: "1px solid #0a0a0a",
                marginTop: "8px",
                justifyContent: "center",
              }}
              onClick={navigateAsDonor}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = `0 0 0 3px rgba(168,181,162,0.35)`; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = "none"; }}
            >
              Start donating
            </button>
          </div>

          {/* Debtor path */}
          <div style={{
            padding: "40px 36px",
            borderRadius: "20px",
            border: `1px solid ${TERRACOTTA}40`,
            background: `${TERRACOTTA}06`,
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}>
            <div style={{
              width: "48px", height: "48px",
              borderRadius: "50%",
              background: `${TERRACOTTA}20`,
              border: `1px solid ${TERRACOTTA}50`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Leaf size={20} color={TERRACOTTA} />
            </div>
            <h2 style={{ fontSize: "22px", fontWeight: 200, letterSpacing: "-0.02em", margin: 0 }}>
              I need help
            </h2>
            <p style={{ fontSize: "14px", fontWeight: 300, color: "hsl(var(--muted-foreground))", lineHeight: 1.75, margin: 0 }}>
              Apply to have your debt reviewed and placed into a community pool. If funded, we work directly with your creditor to settle the balance.
            </p>
            <ul style={{ fontSize: "13px", fontWeight: 300, color: "hsl(var(--muted-foreground))", lineHeight: 2, paddingLeft: "18px", margin: 0 }}>
              <li>Submit your debt documentation</li>
              <li>Get reviewed by our team</li>
              <li>Pool opens to community donors</li>
              <li>Debt settled — fresh start begins</li>
            </ul>
            <button
              style={{
                ...pillBtn,
                background: "transparent",
                color: "#0a0a0a",
                border: "1px solid rgba(10,10,10,0.25)",
                marginTop: "8px",
                justifyContent: "center",
              }}
              onClick={navigateAsDebtor}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(10,10,10,0.05)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
            >
              Apply for relief
            </button>
          </div>
        </div>
      </section>

      <QuoteCarousel />
      <SiteFooter />
    </div>
  );
}
